import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { imageUrl } from '../lib/images';

const PRACTICE_AREAS = [
  {
    title: 'Miljøvurdering',
    href: '/fagomraader/miljoevurdering/',
    description:
      'Udvalgte planer/programmer og projekter skal i henhold til miljøvurderingsloven miljøvurderes og have en tilladelse, inden de vedtages eller gennemføres.',
    icon: 'fad fa-leaf',
  },
  {
    title: 'Natur, Vand & Miljø',
    href: '/fagomraader/natur-vand-miljoe/',
    description:
      'Beskyttelsen af natur, vand og miljø er et centralt område af miljøretten med kompleks regulering. Reglerne har stor betydning for projektudvikling af fx byggeri og infrastruktur.',
    icon: 'fad fa-trees',
  },
  {
    title: 'Planforhold',
    href: '/fagomraader/planforhold/',
    description:
      'Projektudvikling på både land og hav kræver kendskab til de planmæssige krav til arealanvendelsen. Projekter må som udgangspunkt udvikles inden for de rammer, som planerne har fastlagt.',
    icon: 'fad fa-layer-group',
  },
  {
    title: 'Klima & Bæredygtighed',
    href: '/fagomraader/klima-baeredygtighed/',
    description:
      'Kravene til virksomhedernes rapportering af data og brug af bæredygtighedsbegrebet har været stærkt tiltagende. Kravene medfører øget fokus på bæredygtighedstiltag fx i forbindelse med projektudvikling.',
    icon: 'fad fa-recycle',
  },
  {
    title: 'Projektudvikling',
    subtitle: 'Projektudvikling / Bygherrerådgivning',
    href: '/fagomraader/bygherreraadgivning/',
    description:
      'Som bygherre af infrastruktur og byggeri er overholdelse af tidsplan og projektøkonomi af stor betydning. Overblik over myndighedskrav til projektet er væsentligt, såfremt dette skal lykkes.',
    icon: 'fad fa-pencil',
  },
  {
    title: 'Anlægslove, ekspropriation, ledninger mv.',
    href: '/fagomraader/ekspropriation-ledninger-mv/',
    description:
      'Anlægslove giver ofte hjemmel til håndtering af ekspropriation, servitutter og ledninger. Jeg har mange års erfaring med anlægslove og kan bistå mine klienter i forbindelse med ekspropriation mv.',
    icon: 'fad fa-file-signature',
  },
];

export default function Fagomraader() {
  const heroBg = imageUrl('/_assets/imgi-12-29267.webp', 'width=1920,quality=85,format=auto');

  return (
    <div className="bg-white min-h-screen">
      <SEO
        title="Fagområder - Advokatfirmaet Kopp"
        description="Miljøretten består af en lang række love, som tilsammen udgør et kompleks samspil af regler, der regulerer menneskelige aktiviteters påvirkning af det eksterne miljø. Formålet med reguleringen er, at konsekvenserne af aktiviteterne holdes på et samfundsmæssigt acceptabelt niveau."
      />

      {/* Hero Section with dark forest background & overlay */}
      <section className="relative w-full overflow-hidden bg-[#162725] text-white">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('${heroBg}')` }}
        />
        {/* Dark forest / teal overlay matching kopplaw.dk */}
        <div className="absolute inset-0 bg-[#0c1817]/75 sm:bg-[#0c1817]/70" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 sm:pt-28 pb-20 sm:pb-28">
          <div className="max-w-4xl">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-8 sm:mb-10">
              Fagområder
            </h1>
            <div className="space-y-5 text-sm sm:text-base text-slate-200/90 leading-relaxed font-body">
              <p>
                Miljøretten består af en lang række love, som tilsammen udgør et kompleks samspil af regler, der regulerer menneskelige aktiviteters påvirkning af det eksterne miljø. Formålet med reguleringen er, at konsekvenserne af aktiviteterne holdes på et samfundsmæssigt acceptabelt niveau.
              </p>
              <p>
                Bæredygtighedsretten består af FNs Verdensmål suppleret med en stigende grad af EU-drevet regulering. Reguleringen består hovedsageligt af krav om dataindsamling med tilhørende oplysningskrav. Formålet med reguleringen er grundlæggende at reservere bæredygtighedsbegrebet til tiltag og initiativer, som går videre end det, der er lovgivningsmæssigt påkrævet for at afbøde konsekvenserne af menneskelige aktiviteter.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cards Grid Section */}
      <section className="w-full bg-white py-16 sm:py-24 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 items-stretch">
            {PRACTICE_AREAS.map((area, idx) => (
              <div
                key={idx}
                className="bg-[#f6f6f6] p-8 sm:p-9 flex flex-col justify-between border border-slate-200/90 shadow-xs transition-shadow duration-200 hover:shadow-md"
              >
                <div>
                  <div className="mb-6 inline-flex items-center text-[#23495d]">
                    <i className={`${area.icon} text-3xl sm:text-4xl`} aria-hidden="true" />
                  </div>

                  <h2 className="font-heading text-lg sm:text-xl font-bold text-slate-900 mb-4 leading-snug">
                    {area.title}
                  </h2>
                  <p className="font-body text-xs sm:text-sm text-slate-600 leading-relaxed">
                    {area.description}
                  </p>
                </div>

                <div className="pt-8 mt-auto">
                  <Link
                    to={area.href}
                    className="group inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-slate-800 hover:text-[#23495d] transition-colors"
                  >
                    <span className="text-slate-400 group-hover:text-[#23495d] transition-colors">→</span>
                    <span className="hover:underline">Læs mere</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
